// src/pages/HVSwitching.tsx — HV Switching Programmes, cable records, Form 16
import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  MOCK_SWITCHING_PROGRAMMES, MOCK_HV_CABLE_RECORDS,
  SP_STATUS, STEP_PHASE_STYLE,
} from '../utils/mockDataTier5'
import { formatDate } from '../utils/formatters'
import {
  Zap, Plus, ArrowLeft, ChevronRight, AlertTriangle,
  CheckCircle, Clock, Shield, FileText, X, Users, Lock, Download} from 'lucide-react'

type SwitchingStatus = 'DRAFT' | 'NETWORK_APPROVED' | 'ACTIVE' | 'SITE_CLEARED' | 'CLOSED'
type SwitchingStepPhase = 'ISOLATION' | 'TEST_DEAD' | 'EARTH' | 'CLEARANCE' | 'RETURN_CLEARANCE' | 'REMOVE_EARTH' | 'RE_ENERGISE'

type SwitchingProgramme = {
  id: string
  spNumber: string
  jobId: string
  jobNumber: string
  networkOperator: string
  substationName: string
  substationId: string
  voltageKV: number
  equipmentToIsolate: string[]
  isolationType: string
  minimumApproachDistanceM: number
  controlRoomOfficerName: string | null
  controlRoomPhone: string
  authorisationTime: string | null
  authorisationReference: string | null
  leadTechnicianId: string
  leadTechnicianName: string
  leadTechnicianAuthority: string | null
  status: SwitchingStatus
  issueTime: string | null
  completionTime: string | null
  clearanceTime: string | null
  signons: Array<{
    employeeId: string
    employeeName: string
    hvAuthority: string | null
    signedAt: string
  }>
  switchingSteps: Array<{
    stepNumber: number
    action: string
    phase: SwitchingStepPhase
    completedByName: string | null
    completedAt: string | null
    verified: boolean
  }>
}

const CRITICAL_RULES = [
  'Switching Programme MUST be authorised by network control room before any isolation begins',
  'Site MUST be verified dead with approved voltage indicator before touching any conductor',
  'ALL workers must sign on to Switching Programme before entering the work zone',
  'Earths MUST be applied and verified before work commences on isolated conductors',
  'Only the Lead Technician may accept or return clearances to the network operator',
  'HV Authority holder must be on site at all times when HV work is in progress',
  'Form 16 must be issued before any HV installation is energised for the first time',
]

// SP Detail
function HVStepSignOffModal({
  step, spNumber, signerName, onConfirm, onClose,
}: {
  step: SwitchingProgramme['switchingSteps'][0]
  spNumber: string
  signerName: string
  onConfirm: (name: string, notes: string) => void
  onClose: () => void
}) {
  const [name, setName]       = useState(signerName)
  const [notes, setNotes]     = useState('')
  const [declared, setDeclared] = useState(false)
  const now = new Date().toLocaleString('en-AU', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-amber-50">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-600" />Sign off switching step {step.stepNumber}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{spNumber} — {step.phase.replace(/_/g, ' ')}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-900">
            <p className="font-semibold mb-1">{step.action}</p>
            <p className="text-xs text-red-700">Phase: {step.phase.replace(/_/g,' ')}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 leading-relaxed">
            <p className="font-semibold mb-1">Declaration (Electrical Safety Act 2002 (Qld) s.27):</p>
            <p>I confirm that this switching step was performed safely and in accordance with the switching programme, that all required safety checks were completed before and after this action, and that the equipment state described in this step has been verified to be correct.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Authorised person name <span className="text-red-500">*</span></label>
            <input className="input text-sm" value={name} onChange={e => setName(e.target.value)}
              placeholder="Full name of person completing this step" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
            <input className="input text-sm" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Earth applied to bus A and B, tested dead with MIT430..." />
          </div>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={declared} onChange={e => setDeclared(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-amber-600 flex-shrink-0" />
            <span className="text-xs text-gray-700">
              I, <strong>{name || '(name required)'}</strong>, confirm this step was completed safely and the equipment state has been verified.<br />
              <strong className="text-gray-900">Time: {now}</strong>
            </span>
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button onClick={() => name.trim() && declared && onConfirm(name, notes)}
              disabled={!name.trim() || !declared}
              className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50 bg-amber-600 hover:bg-amber-700">
              <CheckCircle className="w-3.5 h-3.5" />Confirm step complete
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// SP Detail
function SPDetail({ sp: initial, onBack }: { sp: SwitchingProgramme; onBack: () => void }) {
  const { isTier } = useAuthStore()
  const [sp, setSP]             = useState(initial)
  const [tab, setTab]           = useState<'steps'|'signons'|'details'>('steps')
  const [showSignOn, setShowSignOn] = useState(false)
  const [signingStep, setSigningStep] = useState<SwitchingProgramme['switchingSteps'][0] | null>(null)
  const ss = SP_STATUS[sp.status] ?? SP_STATUS.DRAFT
  const canAuthorise = isTier('B_PM') && sp.status === 'DRAFT'

  const completedSteps = sp.switchingSteps.filter(s => s.completedAt).length
  const totalSteps     = sp.switchingSteps.length

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Switching programmes
      </button>

      {/* Critical safety banner — always visible */}
      <div className="mb-5 bg-red-700 text-white rounded-xl overflow-hidden">
        <div className="px-5 py-3 flex items-center gap-2 border-b border-red-600">
          <Zap className="w-4 h-4 flex-shrink-0" />
          <span className="font-bold text-sm">HV SAFETY RULES — READ BEFORE PROCEEDING</span>
        </div>
        <div className="px-5 py-3 space-y-1">
          {CRITICAL_RULES.map((r, i) => (
            <div key={i} className="text-xs text-red-100 flex items-start gap-2">
              <span className="font-bold text-red-300 flex-shrink-0">{i + 1}.</span>{r}
            </div>
          ))}
        </div>
      </div>

      {/* SP header */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-5">
        <div className="px-6 py-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-lg font-bold text-gray-900">{sp.spNumber}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ss.bg} ${ss.text}`}>{ss.label}</span>
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold">{sp.voltageKV}kV</span>
                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{sp.jobNumber}</span>
              </div>
              <h1 className="text-base font-medium text-gray-700">{sp.substationName} — {sp.networkOperator}</h1>
              <p className="text-xs text-gray-400 mt-0.5">Min approach distance: {sp.minimumApproachDistanceM}m · Lead technician: {sp.leadTechnicianName} ({sp.leadTechnicianAuthority})</p>
            </div>
            <div className="flex gap-2">
              {canAuthorise && (
                <button onClick={() => setSP(s => ({ ...s, status: 'NETWORK_APPROVED' as const, authorisationTime: new Date().toISOString(), authorisationReference: `ENX-CR-${Date.now()}` }))}
                  className="btn-primary flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4" />Record authorisation
                </button>
              )}
              {sp.status === 'DRAFT' && (
                <button onClick={() => generateAuthorityRequest(sp)} className="btn-secondary flex items-center gap-1.5 text-sm">
                  <FileText className="w-3.5 h-3.5" />Authority request
                </button>
              )}
              <button onClick={() => generateSwitchingProgrammeDocument(sp, CRITICAL_RULES)} className="btn-secondary flex items-center gap-1.5 text-sm">
                <Download className="w-3.5 h-3.5" />Print switching programme
              </button>
              {sp.status === 'NETWORK_APPROVED' && isTier('B_PM') && (
                <button onClick={() => setSP(s => ({ ...s, status: 'ACTIVE' as const, issueTime: new Date().toISOString() }))}
                  className="btn-primary bg-green-600 hover:bg-green-700 flex items-center gap-1.5">
                  <Zap className="w-4 h-4" />Issue switching programme
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 border-t text-sm">
          {[
            { label: 'Network operator', value: sp.networkOperator },
            { label: 'Control room officer', value: sp.controlRoomOfficerName || 'TBC' },
            { label: 'Authorisation ref', value: sp.authorisationReference || '—' },
            { label: 'Steps complete', value: totalSteps > 0 ? `${completedSteps} / ${totalSteps}` : 'Not started' },
          ].map(f => (
            <div key={f.label} className="px-4 py-3">
              <div className="text-xs text-gray-500">{f.label}</div>
              <div className="font-medium mt-0.5">{f.value}</div>
            </div>
          ))}
        </div>
        {sp.equipmentToIsolate.length > 0 && (
          <div className="px-5 py-3 border-t bg-amber-50">
            <div className="text-xs font-semibold text-amber-800 mb-1">Equipment to isolate</div>
            <div className="flex flex-wrap gap-1.5">
              {sp.equipmentToIsolate.map((e, i) => (
                <span key={i} className="text-xs bg-amber-200 text-amber-900 px-2 py-0.5 rounded font-mono">{e}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1">
        {([
          { key: 'steps',   label: `Switching steps (${sp.switchingSteps.length})` },
          { key: 'signons', label: `Sign-ons (${sp.signons.length})` },
          { key: 'details', label: 'Details' },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex-1 text-xs font-medium py-2 px-3 rounded-lg transition-colors ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {tab === 'steps' && (
          sp.switchingSteps.length === 0 ? (
            <div className="py-14 text-center text-gray-400">
              <Lock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No switching steps defined yet</p>
              {isTier('B_PM') && <button className="btn-primary mt-3 text-sm">Add switching steps</button>}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {sp.switchingSteps.map(step => {
                const ph = STEP_PHASE_STYLE[step.phase] ?? STEP_PHASE_STYLE.ISOLATION
                const done = !!step.completedAt
                return (
                  <div key={step.stepNumber} className={`flex items-start gap-4 px-5 py-4 ${done ? '' : 'bg-gray-50/50'}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${done ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                      {done ? '✓' : step.stepNumber}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${ph.bg} ${ph.text}`}>{ph.label}</span>
                        {step.verified && <span className="text-xs text-green-600 font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" />Verified</span>}
                      </div>
                      <p className={`text-sm font-medium ${done ? 'text-gray-900' : 'text-gray-500'}`}>{step.action}</p>
                      {done && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {step.completedByName} · {new Date(step.completedAt!).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true })}
                        </p>
                      )}
                    </div>
                    {!done && sp.status === 'ACTIVE' && (
                      <button onClick={() => setSigningStep(step)}
                        className="flex-shrink-0 flex items-center gap-1 text-xs bg-amber-600 hover:bg-amber-700 text-white px-2.5 py-1.5 rounded-lg font-medium transition-colors">
                        <Zap className="w-3 h-3" />Complete step
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )
        )}

        {tab === 'signons' && (
          <div>
            {sp.status !== 'DRAFT' && sp.status !== 'CLOSED' && (
              <div className="px-5 py-3 border-b bg-blue-50 flex items-center justify-between">
                <span className="text-sm text-blue-800">All workers must sign on before entering the work zone</span>
                <button className="text-xs btn-primary py-1.5">Sign on</button>
              </div>
            )}
            {sp.signons.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm">No workers signed on yet</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {sp.signons.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 px-5 py-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-4 h-4 text-amber-700" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{s.employeeName}</div>
                      <div className="text-xs text-gray-400">HV Authority: {s.hvAuthority}</div>
                    </div>
                    <div className="text-xs text-gray-400">{new Date(s.signedAt).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'details' && (
          <div className="p-5 grid grid-cols-2 gap-4 text-sm">
            {[
              { label: 'Switching programme no.', value: sp.spNumber },
              { label: 'Network operator', value: sp.networkOperator },
              { label: 'Substation', value: sp.substationName },
              { label: 'Asset ID', value: sp.substationId },
              { label: 'Isolation type', value: sp.isolationType.replace(/_/g,' ') },
              { label: 'Min approach distance', value: `${sp.minimumApproachDistanceM}m` },
              { label: 'Control room officer', value: sp.controlRoomOfficerName || '—' },
              { label: 'CR phone', value: sp.controlRoomPhone },
              { label: 'Authorisation ref', value: sp.authorisationReference || '—' },
              { label: 'Issue time', value: sp.issueTime ? new Date(sp.issueTime).toLocaleString('en-AU') : '—' },
              { label: 'Completion time', value: sp.completionTime ? new Date(sp.completionTime).toLocaleString('en-AU') : '—' },
              { label: 'Clearance time', value: sp.clearanceTime ? new Date(sp.clearanceTime).toLocaleString('en-AU') : '—' },
            ].map(f => (
              <div key={f.label}>
                <div className="text-xs text-gray-500">{f.label}</div>
                <div className="font-medium mt-0.5">{f.value}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {signingStep && (
        <HVStepSignOffModal
          step={signingStep}
          spNumber={sp.spNumber}
          signerName={sp.leadTechnicianName}
          onConfirm={(name, notes) => {
            setSP(prev => ({
              ...prev,
              switchingSteps: prev.switchingSteps.map(s =>
                s.stepNumber === signingStep.stepNumber
                  ? { ...s, completedByName: name, completedAt: new Date().toISOString(), verified: true }
                  : s
              ),
            }))
            setSigningStep(null)
          }}
          onClose={() => setSigningStep(null)}
        />
      )}
    </div>
  )
}

// Cable test records
function CableRecords() {
  return (
    <div className="space-y-4">
      {MOCK_HV_CABLE_RECORDS.map(rec => (
        <div key={rec.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-bold text-gray-900">{rec.jobNumber}</span>
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-bold">{rec.voltageClass}</span>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Form 16: {rec.form16Number}</span>
            </div>
            <p className="text-sm text-gray-700 font-medium">{rec.cableType}</p>
            <p className="text-xs text-gray-500 mt-0.5">{rec.routeDescription} · {rec.lengthMetres}m</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 text-sm">
            {[
              { label: 'IR test', value: `${rec.insulationResistanceMOhm} MΩ`, ok: rec.insulationResistanceMOhm > 100 },
              { label: 'Continuity', value: rec.continuityTestResult, ok: rec.continuityTestResult === 'PASS' },
              { label: 'VLF test', value: `${rec.vlfTestResult} @ ${rec.vlfTestVoltageKV}kV / ${rec.vlfTestDurationMin}min`, ok: rec.vlfTestResult === 'PASS' },
              { label: 'Form 16 issued', value: formatDate(rec.form16IssueDate), ok: true },
            ].map(f => (
              <div key={f.label} className="px-4 py-3">
                <div className="text-xs text-gray-500">{f.label}</div>
                <div className={`font-bold mt-0.5 ${f.ok ? 'text-green-700' : 'text-red-700'}`}>{f.value}</div>
              </div>
            ))}
          </div>
          {rec.joints.length > 0 && (
            <div className="px-5 py-3 border-t">
              <div className="text-xs font-semibold text-gray-700 mb-2">Cable joints</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {rec.joints.map(j => (
                  <div key={j.num} className="text-xs bg-gray-50 rounded-lg px-3 py-2">
                    <div className="font-medium text-gray-900">Joint {j.num} — {j.type} — {j.kit}</div>
                    <div className="text-gray-500">{j.location} · Jointer: {j.jointer} ({j.regNum})</div>
                    <div className="text-gray-500">{formatDate(j.date)} · <span className={j.result === 'PASS' ? 'text-green-700 font-bold' : 'text-red-700 font-bold'}>{j.result}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// Main page


// ─────────────────────────────────────────────────────────────────
// PHASE 8 — HV SWITCHING PROGRAMME (formal document)
// Full switching steps sequence with safety rules, equipment to
// isolate, and a sign-on register — for on-site use during HV work.
// ─────────────────────────────────────────────────────────────────
function generateSwitchingProgrammeDocument(sp: any, criticalRules: string[]) {
  const now = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtDT = (iso: string | null) => iso ? new Date(iso).toLocaleString('en-AU', { dateStyle:'medium', timeStyle:'short' }) : '—'

  const stepRows = sp.switchingSteps.map((s: any) => `
    <tr style="background:${s.phase==='ISOLATION'?'#fef2f2':s.phase==='EARTH'?'#fffbeb':s.phase==='CLEARANCE'?'#f0fdf4':s.phase==='RE_ENERGISE'?'#eff6ff':'#fafafa'}">
      <td style="padding:6px 8px;text-align:center;font-weight:bold">${s.stepNumber}</td>
      <td style="padding:6px 8px;font-size:8.5pt;font-weight:bold;color:#555">${s.phase.replace(/_/g,' ')}</td>
      <td style="padding:6px 10px;font-size:9.5pt">${s.action}</td>
      <td style="padding:6px 8px;text-align:center">${s.completedAt ? `✓ ${s.completedByName}` : '☐'}</td>
    </tr>`).join('')

  const signonRows = sp.signons.map((s: any, i: number) => `
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:6px 10px">${s.employeeName}</td>
      <td style="padding:6px 10px;font-family:monospace;font-size:9pt">${s.hvAuthority}</td>
      <td style="padding:6px 8px;text-align:center;font-size:9pt">${new Date(s.signedAt).toLocaleTimeString('en-AU', {hour:'2-digit',minute:'2-digit'})}</td>
    </tr>`).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Switching Programme — ${sp.spNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}
h1{font-size:18pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:10.5pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:14px 0 8px}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}
.hv-banner{background:#b45309;color:#fff;text-align:center;padding:8px 16px;border-radius:4px;font-weight:bold;margin-bottom:12px}
.info table{width:100%;border-collapse:collapse;margin-bottom:10px}
.info td{padding:4px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:34%;font-weight:bold;color:#555;background:#f9fafb}
table.steps{width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:14px}
th{background:#1B3A6B;color:#fff;padding:6px 8px;text-align:left}
.rules-box{background:#fef2f2;border:2px solid #fca5a5;border-radius:5px;padding:10px 16px;margin-bottom:12px}
.rules-box li{margin:4px 0;font-size:9.5pt;color:#7f1d1d}
.equip-box{background:#fef3c7;border:1px solid #fbbf24;border-radius:4px;padding:10px 14px;margin-bottom:12px}
.equip-chip{display:inline-block;background:#fde68a;color:#78350f;font-family:monospace;font-size:9pt;padding:3px 8px;border-radius:4px;margin:2px}
.sig-row{display:flex;gap:30px;margin-top:14px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:28px;margin-bottom:4px}.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:10mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 44 128 993 087 · 14 Commerce Dr, Salisbury QLD 4107</div></div>
  <div style="text-align:right"><div style="font-size:15pt;font-weight:900;color:#1B3A6B">HV SWITCHING PROGRAMME</div><div style="font-family:monospace;font-weight:bold;color:#1B3A6B">${sp.spNumber}</div><div style="font-size:9pt;color:#888">${now}</div></div>
</div>

<div class="hv-banner">⚡ HIGH VOLTAGE WORK — ${sp.voltageKV}kV — NETWORK OPERATOR AUTHORISATION MANDATORY</div>

<div class="info"><table>
  <tr><td>Network operator</td><td><strong>${sp.networkOperator}</strong></td><td>Substation</td><td>${sp.substationName} (${sp.substationId})</td></tr>
  <tr><td>Job reference</td><td>${sp.jobNumber}</td><td>Voltage</td><td><strong>${sp.voltageKV}kV</strong></td></tr>
  <tr><td>Isolation type</td><td>${sp.isolationType.replace(/_/g,' ')}</td><td>Min approach distance</td><td>${sp.minimumApproachDistanceM}m</td></tr>
  <tr><td>Lead technician</td><td>${sp.leadTechnicianName}</td><td>HV authority</td><td>${sp.leadTechnicianAuthority}</td></tr>
  <tr><td>Control room officer</td><td>${sp.controlRoomOfficerName || 'TBC'}</td><td>CR phone</td><td>${sp.controlRoomPhone}</td></tr>
  <tr><td>Authorisation reference</td><td colspan="3"><strong>${sp.authorisationReference || 'PENDING — see Authority Request'}</strong> ${sp.authorisationTime ? '· ' + fmtDT(sp.authorisationTime) : ''}</td></tr>
</table></div>

${sp.equipmentToIsolate.length > 0 ? `<div class="equip-box">
  <div style="font-size:9.5pt;font-weight:bold;color:#92400e;margin-bottom:6px">Equipment to isolate</div>
  ${sp.equipmentToIsolate.map((e: string) => `<span class="equip-chip">${e}</span>`).join('')}
</div>` : ''}

<div class="rules-box">
  <div style="font-weight:bold;color:#b91c1c;margin-bottom:6px">⚠ CRITICAL SAFETY RULES</div>
  <ul style="margin:0;padding-left:20px">${criticalRules.map(r => `<li>${r}</li>`).join('')}</ul>
</div>

<h2>Switching steps sequence</h2>
<table class="steps">
  <thead><tr><th style="width:6%;text-align:center">#</th><th style="width:14%">Phase</th><th style="width:60%">Action</th><th style="width:20%;text-align:center">Completed</th></tr></thead>
  <tbody>${stepRows || '<tr><td colspan="4" style="padding:12px;text-align:center;color:#9ca3af">No switching steps defined yet</td></tr>'}</tbody>
</table>

<h2>Worker sign-on register</h2>
<table class="steps">
  <thead><tr><th>Worker name</th><th>HV authority</th><th style="text-align:center">Time signed on</th></tr></thead>
  <tbody>${signonRows || '<tr><td colspan="3" style="padding:12px;text-align:center;color:#9ca3af">No workers signed on</td></tr>'}
  ${['','',''].map(()=>'<tr><td style="padding:20px 10px;border-bottom:1px solid #e5e7eb"></td><td style="border-bottom:1px solid #e5e7eb"></td><td style="border-bottom:1px solid #e5e7eb;text-align:center"></td></tr>').join('')}
  </tbody>
</table>

<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Lead Technician — ${sp.leadTechnicianName} · date</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Network operator authorisation — name &amp; reference</div></div>
</div>
<div class="footer">MCB Civil Electrical · HV Switching Programme · ${sp.spNumber} · ${sp.jobNumber} · Generated ${now}</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1020,height=820')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 8 — AUTHORITY REQUEST (to network operator control room)
// Formal request seeking written/verbal authorisation to isolate
// HV equipment before switching work can begin.
// ─────────────────────────────────────────────────────────────────
function generateAuthorityRequest(sp: any) {
  const now = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Authority Request — ${sp.spNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11pt;color:#111;margin:22mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:flex-end}
p{line-height:1.85;margin-bottom:10px}
.req-banner{background:#b45309;color:#fff;text-align:center;padding:10px 16px;border-radius:5px;margin:14px 0;font-weight:bold}
.info table{width:100%;border-collapse:collapse;margin-bottom:14px}
.info td{padding:5px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:38%;font-weight:bold;color:#555;background:#f9fafb}
.equip-box{background:#fef3c7;border:1px solid #fbbf24;border-radius:4px;padding:10px 14px;margin:12px 0}
.equip-chip{display:inline-block;background:#fde68a;color:#78350f;font-family:monospace;font-size:9pt;padding:3px 8px;border-radius:4px;margin:2px}
.response-box{border:2px solid #1B3A6B;border-radius:5px;padding:14px 18px;margin-top:18px}
.sig-line{border-bottom:1.5px solid #333;height:32px;margin:14px 0 4px}
.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:18px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:16mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 44 128 993 087 · 14 Commerce Dr, Salisbury QLD 4107 · 07 3344 5500</div></div>
  <div style="text-align:right"><div style="font-size:9pt;color:#888">Date: ${now}</div><div style="font-size:9pt;color:#888">Ref: ${sp.spNumber}</div></div>
</div>

<p><strong>${sp.networkOperator} Network Control Room</strong></p>
<br>
<div class="req-banner">⚡ REQUEST FOR HV SWITCHING AUTHORISATION</div>

<p>Dear Control Room Officer,</p>

<p>MCB Civil Electrical Pty Ltd requests authorisation to isolate the high voltage equipment described below to enable safe execution of the switching programme referenced. No isolation work will commence until written or verbal authorisation and a control room reference number have been received.</p>

<div class="info"><table>
  <tr><td>Switching programme reference</td><td><strong>${sp.spNumber}</strong></td></tr>
  <tr><td>Job reference</td><td>${sp.jobNumber}</td></tr>
  <tr><td>Substation</td><td>${sp.substationName} (${sp.substationId})</td></tr>
  <tr><td>Voltage</td><td><strong>${sp.voltageKV}kV</strong></td></tr>
  <tr><td>Isolation type requested</td><td>${sp.isolationType.replace(/_/g,' ')}</td></tr>
  <tr><td>Minimum approach distance</td><td>${sp.minimumApproachDistanceM}m</td></tr>
  <tr><td>Lead technician</td><td>${sp.leadTechnicianName}</td></tr>
  <tr><td>HV authority number</td><td>${sp.leadTechnicianAuthority}</td></tr>
  <tr><td>Requested isolation window</td><td>To be confirmed by phone with control room — see contact below</td></tr>
</table></div>

${sp.equipmentToIsolate.length > 0 ? `<div class="equip-box">
  <div style="font-size:9.5pt;font-weight:bold;color:#92400e;margin-bottom:6px">Equipment requested for isolation</div>
  ${sp.equipmentToIsolate.map((e: string) => `<span class="equip-chip">${e}</span>`).join('')}
</div>` : ''}

<p>MCB Civil Electrical confirms that the lead technician named above holds a current HV authority appropriate to this voltage class and that all crew members will sign on to the switching programme and comply with the network operator's safety rules and lockout/tagout procedures before entering the isolated work zone.</p>

<p>Please contact our lead technician on confirmation of authorisation to coordinate the isolation timing: ${sp.leadTechnicianName} · HV Authority ${sp.leadTechnicianAuthority}.</p>

<div class="response-box">
  <p style="font-weight:bold;color:#1B3A6B;margin-bottom:10px">Control room response (to be completed by network operator)</p>
  <p>Authorisation reference: _________________________</p>
  <p>Authorised by: _________________________ &nbsp;&nbsp; Date/time: _________________________</p>
  <p>Permitted isolation window: _________________________ to _________________________</p>
  <div class="sig-line"></div>
  <div class="sig-lbl">Control room officer — name &amp; signature</div>
</div>

<div class="footer">MCB Civil Electrical Pty Ltd · Authority Request · ${sp.spNumber} · ${sp.jobNumber} · Generated ${now}</div>
</body></html>`
  const w = window.open('', '_blank', 'width=960,height=820')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}
export function HVSwitching() {
  const { isTier } = useAuthStore()
  const [sps, setSPs]         = useState<SwitchingProgramme[]>(MOCK_SWITCHING_PROGRAMMES as SwitchingProgramme[])
  const [selected, setSelected] = useState<SwitchingProgramme | null>(null)
  const [view, setView]         = useState<'programmes'|'cable_records'>('programmes')

  if (selected) return <SPDetail sp={selected} onBack={() => setSelected(null)} />

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HV / Switching</h1>
          <p className="text-sm text-gray-500 mt-0.5">Switching programmes, cable test records, Form 16</p>
        </div>
        {isTier('B_PM') && <button className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" />New switching programme</button>}
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        <button onClick={() => setView('programmes')} className={`text-sm font-medium px-5 py-2 rounded-lg transition-colors flex items-center gap-2 ${view==='programmes' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          <Zap className="w-4 h-4" />Switching programmes ({sps.length})
        </button>
        <button onClick={() => setView('cable_records')} className={`text-sm font-medium px-5 py-2 rounded-lg transition-colors flex items-center gap-2 ${view==='cable_records' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          <FileText className="w-4 h-4" />Cable test records ({MOCK_HV_CABLE_RECORDS.length})
        </button>
      </div>

      {view === 'cable_records' && <CableRecords />}

      {view === 'programmes' && (
        <div className="space-y-3">
          {/* Safety notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              <strong>HV work — network operator authorisation mandatory.</strong> No switching programme may be issued until the network control room has provided written/verbal authorisation and a control room reference number.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
            {sps.length === 0 ? (
              <div className="py-14 text-center text-gray-400"><Zap className="w-8 h-8 mx-auto mb-2 text-gray-300" /><p className="text-sm">No switching programmes</p></div>
            ) : sps.map(sp => {
              const ss = SP_STATUS[sp.status] ?? SP_STATUS.DRAFT
              const completed = sp.switchingSteps.filter(s => s.completedAt).length
              return (
                <div key={sp.id} onClick={() => setSelected(sp)} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 cursor-pointer group">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${ss.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-semibold text-sm text-gray-900">{sp.spNumber}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${ss.bg} ${ss.text}`}>{ss.label}</span>
                      <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full font-bold">{sp.voltageKV}kV</span>
                      <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full">{sp.jobNumber}</span>
                    </div>
                    <p className="text-sm text-gray-600 truncate">{sp.substationName} · {sp.networkOperator}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Lead: {sp.leadTechnicianName} · {sp.switchingSteps.length > 0 ? `${completed}/${sp.switchingSteps.length} steps` : 'Steps not yet defined'}</p>
                  </div>
                  {sp.issueTime && (
                    <div className="hidden sm:block text-right flex-shrink-0 text-xs text-gray-400 w-28">
                      <div>Issued</div>
                      <div>{new Date(sp.issueTime).toLocaleDateString('en-AU',{day:'numeric',month:'short'})}</div>
                    </div>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
